// media-layer/utils/wav-encoder.ts
// Encode raw mono PCM (Float32, -1..1) into a 16-bit little-endian WAV Blob.
//
// WAV/PCM is uncompressed but plays natively on every target — desktop browsers,
// Android, iOS Safari/WKWebView, and native AVFoundation — so a clip recorded on
// any device plays everywhere. That universality (not size) is why the voice
// recorder emits WAV. Capture happens via Web Audio (see useVoiceRecorder), since
// MediaRecorder cannot output WAV on any browser.

/** Voice is mono speech — 16 kHz keeps it intelligible while bounding file size. */
export const WAV_TARGET_SAMPLE_RATE = 16000

const BITS_PER_SAMPLE = 16
const BYTES_PER_SAMPLE = BITS_PER_SAMPLE / 8
const WAV_HEADER_BYTES = 44
const PCM_FORMAT = 1
const NUM_CHANNELS = 1

/**
 * Box-average downsample from `inputSampleRate` to `targetSampleRate`. Returns the
 * input unchanged when the target is not lower (we never upsample). Pure.
 */
export function downsamplePcm(samples: Float32Array, inputSampleRate: number, targetSampleRate: number): Float32Array {
  if (samples.length === 0 || !(inputSampleRate > 0) || targetSampleRate >= inputSampleRate) {
    return samples
  }
  const ratio = inputSampleRate / targetSampleRate
  const outLength = Math.floor(samples.length / ratio)
  const result = new Float32Array(outLength)
  for (let i = 0; i < outLength; i++) {
    const start = Math.floor(i * ratio)
    const end = Math.min(Math.floor((i + 1) * ratio), samples.length)
    let sum = 0
    let count = 0
    for (let j = start; j < end; j++) {
      sum += samples[j]
      count += 1
    }
    result[i] = count > 0 ? sum / count : 0
  }
  return result
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i))
  }
}

/**
 * Encode mono Float32 PCM into a 16-bit WAV Blob, downsampling to
 * `targetSampleRate` first. Samples outside -1..1 are clamped.
 */
export function encodeWavPcm16(samples: Float32Array, inputSampleRate: number, targetSampleRate: number = WAV_TARGET_SAMPLE_RATE): Blob {
  const pcm = downsamplePcm(samples, inputSampleRate, targetSampleRate)
  const rate = targetSampleRate >= inputSampleRate && inputSampleRate > 0 ? inputSampleRate : targetSampleRate
  const dataBytes = pcm.length * BYTES_PER_SAMPLE
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes)
  const view = new DataView(buffer)

  // RIFF chunk descriptor
  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, WAV_HEADER_BYTES - 8 + dataBytes, true)
  writeAscii(view, 8, 'WAVE')

  // fmt sub-chunk
  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // PCM fmt chunk size
  view.setUint16(20, PCM_FORMAT, true)
  view.setUint16(22, NUM_CHANNELS, true)
  view.setUint32(24, rate, true)
  view.setUint32(28, rate * NUM_CHANNELS * BYTES_PER_SAMPLE, true) // byte rate
  view.setUint16(32, NUM_CHANNELS * BYTES_PER_SAMPLE, true) // block align
  view.setUint16(34, BITS_PER_SAMPLE, true)

  // data sub-chunk
  writeAscii(view, 36, 'data')
  view.setUint32(40, dataBytes, true)

  let offset = WAV_HEADER_BYTES
  for (let i = 0; i < pcm.length; i++) {
    const clamped = Math.max(-1, Math.min(1, pcm[i]))
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    offset += BYTES_PER_SAMPLE
  }

  return new Blob([view], { type: 'audio/wav' })
}

/** Conservative encoded 16-bit mono WAV size for `durationSec` at `sampleRate`. */
export function wavPcm16ByteLength(durationSec: number, sampleRate: number = WAV_TARGET_SAMPLE_RATE): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return WAV_HEADER_BYTES
  return WAV_HEADER_BYTES + Math.ceil(durationSec * sampleRate) * BYTES_PER_SAMPLE
}

/** Longest whole-second clip whose encoded WAV stays ≤ `maxBytes`. */
export function maxWavPcm16DurationSec(maxBytes: number, sampleRate: number = WAV_TARGET_SAMPLE_RATE): number {
  const bytesPerSecond = sampleRate * BYTES_PER_SAMPLE
  if (!Number.isFinite(maxBytes) || maxBytes <= WAV_HEADER_BYTES || bytesPerSecond <= 0) return 0
  return Math.floor((maxBytes - WAV_HEADER_BYTES) / bytesPerSecond)
}
